import { clientIdHeader } from '../auth/clientId';
import { authEnabled } from '../auth/config';
import { rememberCreatorToken } from '../auth/creatorToken';
import type {
  AdminAccount, AdminAds, CompatAiResponse, CompatAiStatus, CompatResponse, CompatScenesResponse, TypePairGuide, AdminContact, AdminLimits, AdminLog, AdminReportGroup, AdminResult, AdminStats, AdminTheme, BigFiveSet, ReportReason,
  ContactCategory, CreateThemeResponse, DiagnosisResult, MeResponse, Mode, MyContact, MyResult, MyTheme,
  PolicyVersions, QuestionSet, ResultPageResponse, ReusableAnswers, SessionCreated, SubmitResponse, TermsStatus, ThemeImagesResponse,
  AffiliateItem, SiteConfig, ThemeSummary, ThemeView, AdminVideo, AdminVideos, NewVideo, VideoChemistry, CastRef,
} from './types';

/** 診断 API の基点。Cloud Run 同一オリジンなら /v1。 */
const apiBase = (import.meta.env.VITE_API_BASE_URL ?? '').trim().replace(/\/+$/, '') || '/v1';
/**
 * 結果ページの API（`GET /r/:resultId`）の基点。
 * `/r/:resultId` は SPA のルートと同じパスなので、フロントからは `/api` 付きで呼ぶ
 * （サーバは両方受ける。services/api/src/server.ts）。
 */
const resultPageBase = (apiBase.replace(/\/v1$/, '') || '') + '/api';
/** 測定の土台（設問 JSON）の基点。全テーマ共通。版がパスに入るので、更新は新しいパスの配置で行う。 */
const assessmentBase = (import.meta.env.VITE_ASSESSMENT_BASE_URL ?? '').trim().replace(/\/+$/, '') || '/assessment/v2';
/** 画像の基点。同一オリジンで配るので既定は相対。 */
const assetBase = ((import.meta.env.VITE_ASSET_BASE_URL ?? '').trim().replace(/\/+$/, '') || '') + '/';

export const assetUrl = (path: string) => `${assetBase}${path.replace(/^\/+/, '')}`;

export class ApiError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * ログイン中の ID トークンの取り出し口。AuthProvider が起動時に登録する（apps/web/src/auth/AuthProvider.tsx）。
 * ここでは firebase SDK を import しない（初期バンドルに乗せないため）。
 */
let tokenProvider: (() => Promise<string | null>) | null = null;
export function setAuthTokenProvider(provider: () => Promise<string | null>): void {
  tokenProvider = provider;
}

/**
 * `required`  ログインが要る経路（絵・自分のページ・管理メニュー）。トークンが無ければ 401 を先に投げる
 * `optional`  ログインしていれば添える（テーマ作成・入口ページ・削除）
 */
type AuthMode = 'required' | 'optional';

async function authHeader(mode: AuthMode | undefined): Promise<Record<string, string>> {
  if (!mode) return {};
  const token = await tokenProvider?.().catch(() => null);
  if (token) return { authorization: `Bearer ${token}` };
  if (mode === 'required') throw new ApiError('UNAUTHORIZED', 'ログインしてからもう一度お試しください。');
  return {};
}

/**
 * App Check（doc/AUTH.md §22）。サイトキーは index.html の設定（`window.__GENDAN_CONFIG__`）で届く。
 * 無いサイト（手元の `npm run dev` など）では何もしない。
 */
const appCheckSiteKey = (): string | null => (authEnabled ? window.__GENDAN_CONFIG__?.appCheck?.siteKey ?? null : null);
/** reCAPTCHA が読めない（広告ブロッカーなど）ときに、送信を待たせすぎない。 */
const APP_CHECK_WAIT_MS = 5000;

/** 作成欄を開いたときに呼ぶ。reCAPTCHA を先に読ませておくと、送るときに待たないうえ、点数も付きやすい。 */
export function prepareAppCheck(): void {
  const siteKey = appCheckSiteKey();
  if (siteKey) void import('../auth/appCheck').then((m) => m.startAppCheck(siteKey)).catch(() => undefined);
}

async function appCheckHeader(wanted: boolean | undefined): Promise<Record<string, string>> {
  const siteKey = wanted ? appCheckSiteKey() : null;
  if (!siteKey) return {};
  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), APP_CHECK_WAIT_MS));
  const token = await Promise.race([import('../auth/appCheck').then((m) => m.appCheckToken(siteKey)).catch(() => null), timeout]);
  return token ? { 'x-firebase-appcheck': token } : {};
}

/** `appCheck: true` はお金がかかる POST（テーマ作成・絵・相性の AI・問い合わせ・通報・動画のリクエスト）。 */
async function request<T>(url: string, init?: RequestInit & { auth?: AuthMode; appCheck?: boolean }): Promise<T> {
  let response: Response;
  try {
    const [auth, appCheck] = await Promise.all([authHeader(init?.auth), appCheckHeader(init?.appCheck)]);
    response = await fetch(url, {
      ...init,
      headers: { 'content-type': 'application/json', accept: 'application/json', ...auth, ...appCheck, ...(init?.headers ?? {}) },
    });
  } catch {
    throw new ApiError('NETWORK', '通信できませんでした。電波の良いところでもう一度お試しください。');
  }
  if (response.status === 204) return undefined as T;
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (body as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(error?.code ?? 'INTERNAL_ERROR', error?.message ?? '一時的な不具合が起きました。');
  }
  return body as T;
}

const v1 = <T>(path: string, init?: RequestInit & { auth?: AuthMode; appCheck?: boolean }) => request<T>(`${apiBase}${path}`, init);

/** 結果の PDF（画面と同じ中身・キャラの絵つき・著作権表示つき）。リンクで開けばダウンロードになる。 */
export const resultPdfUrl = (resultId: string) => `${apiBase}/results/${encodeURIComponent(resultId)}/pdf`;

let questionSetPromise: Promise<QuestionSet> | null = null;
let bigFiveSetPromise: Promise<BigFiveSet> | null = null;

function fetchStaticJson<T>(name: string): Promise<T> {
  return fetch(`${assessmentBase}/${name}`).then((response) => {
    if (!response.ok) throw new ApiError('THEME_UNAVAILABLE', '設問を読み込めませんでした。時間をおいてお試しください。');
    return response.json() as Promise<T>;
  });
}

/**
 * 設問セットは API ではなく静的 JSON から取る。診断開始時に必ず叩かれる最頻アクセス。
 * 深さモード（quick / standard / deep）は questions.json、ビッグファイブ版は bigfive.json を同じ形に揃えて返す
 * （画面は `modes[mode].questionIds` と `questions` の id・text しか見ない）。
 */
export function fetchQuestionSet(mode: Mode = 'deep'): Promise<QuestionSet> {
  if (mode === 'bigfive') return fetchBigFiveSet().then(toQuestionSet);
  questionSetPromise ??= fetchStaticJson<QuestionSet>('questions.json').catch((error: unknown) => {
    questionSetPromise = null;
    throw error;
  });
  return questionSetPromise;
}

/** ビッグファイブ版の質問紙（因子・側面の名前と説明文もここ。結果ページとヘルプが読む）。 */
export function fetchBigFiveSet(): Promise<BigFiveSet> {
  bigFiveSetPromise ??= fetchStaticJson<BigFiveSet>('bigfive.json').catch((error: unknown) => {
    bigFiveSetPromise = null;
    throw error;
  });
  return bigFiveSetPromise;
}

function toQuestionSet(set: BigFiveSet): QuestionSet {
  return {
    version: set.version,
    scale: set.scale,
    modes: { bigfive: { label: set.title, questionIds: set.questionIds } },
    questions: set.questions.map((q) => ({ id: q.id, facet: q.facet, axis: q.domain, direction: q.keyed === 1 ? '+' : '-', modes: ['bigfive'], text: q.text, order: q.order })),
    followups: [],
  };
}

export interface AnswerInput {
  readonly questionId: string;
  readonly value: number;
  readonly elapsedMs: number;
}

export interface SessionOptions {
  /** どの診断か。設問は全テーマ共通で、見立てるキャラだけが変わる。 */
  readonly themeId: string;
  /** 再受検なら前回の結果 ID（結果ページの「同じ深さでもう一度」）。 */
  readonly retestOf?: string;
  /** 過去の結果の回答を写して始める（要ログイン。自分の結果だけ）。写せなかった設問だけ答える。 */
  readonly reuseFrom?: string;
}

export function createSession(mode: Mode, referrer: string, options: SessionOptions) {
  const { themeId, retestOf, reuseFrom } = options;
  return v1<SessionCreated>('/sessions', {
    method: 'POST',
    // 回答を写すときだけログインが要る（自分の結果しか写せない）
    ...(reuseFrom ? { auth: 'required' as const } : {}),
    body: JSON.stringify({ mode, referrer, themeId, ...(reuseFrom ? { reuseFrom } : retestOf ? { retestOf } : {}) }),
  });
}

export function submit(
  sessionId: string,
  payload: {
    answers?: readonly AnswerInput[];
    followupAnswers?: readonly { followupId: string; choice: 'A' | 'B' }[];
    idempotencyKey: string;
  },
) {
  // ログインしていれば添える。サーバはこれで結果に uid を付け、`/me` の「受けた診断」に並べ、回答の使い回しを許す
  return v1<SubmitResponse>(`/sessions/${sessionId}/submit`, { method: 'POST', auth: 'optional', body: JSON.stringify(payload) });
}

export const fetchResult = (resultId: string) => v1<DiagnosisResult>(`/results/${resultId}`);

export const sendFeedback = (resultId: string, fit: 'yes' | 'no') =>
  v1<void>(`/results/${resultId}/feedback`, { method: 'POST', body: JSON.stringify({ fit }) });

/** 併存尺度（研究協力・任意）。20 項目すべて 1〜5。 */
export const sendValidation = (resultId: string, answers: Record<string, number>) =>
  v1<void>(`/results/${resultId}/validation`, { method: 'POST', body: JSON.stringify({ scale: 'mini-ipip', answers }) });

/** 相性の詳細版（結果どうし）。a がいま開いている結果（視点）、b が相手の結果。要ログイン。 */
export const fetchCompat = (a: string, b: string) =>
  v1<CompatResponse>(`/compat?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`, { auth: 'required' });

/** 関係に合わせた AI の読みのキャッシュと、今日の残り（数えない）。 */
export const fetchCompatAi = (a: string, b: string, rel: string) =>
  v1<CompatAiStatus>(`/compat/ai?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}&rel=${encodeURIComponent(rel)}`, { auth: 'required' });

/** AI の読みを頼む（Gemini。読み済みの組み合わせは数えない）。 */
export const requestCompatAi = (a: string, b: string, rel: string) =>
  v1<CompatAiResponse>('/compat/ai', { method: 'POST', auth: 'required', appCheck: true, body: JSON.stringify({ a, b, rel }) });

/** AI の読みの 3 つの場面をエピソードの絵にする（裏で描く。描き済みなら数えない）。 */
export const requestCompatScenes = (a: string, b: string, rel: string) =>
  v1<CompatScenesResponse>('/compat/scenes', { method: 'POST', auth: 'required', appCheck: true, body: JSON.stringify({ a, b, rel }) });

/**
 * 詳細版の PDF をダウンロードする。要ログインなので、リンクではなく ID トークンを添えて取り、ファイルとして保存させる。
 * rel を渡すと、その関係の AI の読み（読み済みのときだけ）も載る。型どうしの一般論は PDF にしない。
 */
export async function downloadCompatPdf(a: string, b: string, rel: string | null): Promise<void> {
  const query = `a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}${rel ? `&rel=${encodeURIComponent(rel)}` : ''}`;
  let response: Response;
  try {
    response = await fetch(`${apiBase}/compat/pdf?${query}`, { headers: await authHeader('required') });
  } catch {
    throw new ApiError('NETWORK', '通信できませんでした。電波の良いところでもう一度お試しください。');
  }
  if (!response.ok) {
    const error = ((await response.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(error?.code ?? 'INTERNAL_ERROR', error?.message ?? 'PDF を作れませんでした。');
  }
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = 'gendan-compat.pdf';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** 型（主軸 4 文字）どうしの付き合い方。 */
export const fetchTypePair = (a: string, b: string) =>
  v1<{ guide: TypePairGuide }>(`/compat/types?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`);

/** 結果ページ（結果と、広告を出してよいか）。 */
export const fetchResultPage = (resultId: string) =>
  request<ResultPageResponse>(`${resultPageBase}/r/${resultId}`);

/** 流入元。 */
export function detectReferrer(): string {
  const fromQuery = new URLSearchParams(window.location.search).get('ref');
  if (fromQuery && ['x', 'instagram', 'tiktok', 'qr', 'direct'].includes(fromQuery)) return fromQuery;
  const source = document.referrer;
  if (/(^|\.)(twitter|x)\.com/.test(source)) return 'x';
  if (/instagram\.com/.test(source)) return 'instagram';
  if (/tiktok\.com/.test(source)) return 'tiktok';
  return 'direct';
}

// ---------------------------------------------------------------- 診断テーマ（プロンプトから生成）

/**
 * トップの「自分で作る」欄。プロンプトとキャラ数（既定 16）を送ると Gemini がキャラを考え、診断がひとつ生まれる。
 * 生成には 30〜100 秒かかるので、画面側で待ち表示を出す。文面はそのまま送るので個人情報を書かないよう促す。
 * キャラ数はサーバが 4・8・16・32 に丸める（応答の typeCount / requestedCount）。
 */
export async function createTheme(prompt: string, count: number): Promise<CreateThemeResponse> {
  const created = await v1<CreateThemeResponse>('/themes', {
    method: 'POST',
    auth: 'optional',
    appCheck: true,
    // ログインしていない人の「1 日 3 回」を数える相手（doc/AUTH.md §1）。ログイン済みなら使われない
    headers: clientIdHeader(),
    body: JSON.stringify({ prompt, count, referrer: detectReferrer() }),
  });
  // ログインせずに作ったときだけ合言葉が返る。あとでログインして絵を付けるために覚えておく
  rememberCreatorToken(created.themeId, created.creatorToken);
  return created;
}

export const fetchThemes = () => v1<{ themes: readonly ThemeSummary[] }>('/themes');

/**
 * 診断を通報する（doc/ADS.md §5）。ログイン不要。送った人は残らず、ブラウザの識別子は 1 日の回数を数えるのにだけ使う。
 * 結果ページから送るときは、その結果 ID を添える。
 */
export const reportTheme = (themeId: string, input: { reason: ReportReason; note: string; resultId?: string }) =>
  v1<{ received: true }>(`/themes/${encodeURIComponent(themeId)}/report`, {
    method: 'POST',
    appCheck: true,
    headers: clientIdHeader(),
    body: JSON.stringify(input),
  });

/** ログインしていれば、その人が消せるか・絵を付けられるか（`viewer`）も一緒に返る。 */
export const fetchTheme = (themeId: string) => v1<ThemeView>(`/themes/${encodeURIComponent(themeId)}`, { auth: 'optional' });

/** 診断を消す（論理削除）。作った人がいないテーマはログインなしでも通る。 */
export const deleteTheme = (themeId: string) =>
  v1<{ themeId: string; deletedAt: string; deletedBy: string }>(`/themes/${encodeURIComponent(themeId)}`, { method: 'DELETE', auth: 'optional' });

/**
 * 絵を描く・描き直す（要ログイン。1 日 3 回まで）。
 * `creatorToken` はログインせずに作った診断を引き取るときに添える。
 */
export const requestThemeImages = (themeId: string, options: { force?: boolean; creatorToken?: string } = {}) =>
  v1<ThemeImagesResponse>(`/themes/${encodeURIComponent(themeId)}/images`, {
    method: 'POST',
    auth: 'required',
    appCheck: true,
    body: JSON.stringify(options),
  });

// ---------------------------------------------------------------- ログイン本人と管理メニュー（doc/AUTH.md）

export const fetchMe = () => v1<MeResponse>('/me', { auth: 'required' });

// ---------------------------------------------------------------- 利用規約・プライバシーポリシー（doc/AUTH.md §13）

/** いまの版と最終更新日。本文の頭に出す。ログイン不要。 */
export const fetchPolicies = () => v1<PolicyVersions>('/policies');

/** このサイトで使える機能と広告（doc/ADS.md §1）。本番は index.html に埋め込まれているので、これを呼ぶのは開発中だけ。 */
export const fetchSiteConfig = () => v1<SiteConfig>('/config');

/** 同意した記録を残す（初回ログインの直後と、版が上がったとき）。 */
export const agreeToTerms = () =>
  v1<{ terms: TermsStatus }>('/me/terms', { method: 'POST', auth: 'required', body: JSON.stringify({ agree: true }) });

// ---------------------------------------------------------------- 問い合わせ（doc/AUTH.md §14）

export interface ContactInput {
  readonly category: ContactCategory;
  readonly message: string;
  /** 関連する結果ページの URL か ID（任意）。 */
  readonly result?: string;
  /** 返信先（任意）。書かなければ持たない。 */
  readonly replyTo?: string;
}

/** 問い合わせを送る（要ログイン。1 人 1 日 5 通まで）。 */
export const sendContact = (input: ContactInput) =>
  v1<{ contactId: string; createdAt: string; status: 'open' | 'done' }>('/contact', {
    method: 'POST',
    auth: 'required',
    appCheck: true,
    body: JSON.stringify(input),
  });

/** 自分が送った問い合わせの控え。 */
export const fetchMyContacts = () => v1<{ contacts: readonly MyContact[] }>('/me/contacts', { auth: 'required' });

export const fetchMyThemes = () => v1<{ themes: readonly MyTheme[] }>('/me/themes', { auth: 'required' });

export const fetchMyResults = () => v1<{ results: readonly MyResult[] }>('/me/results', { auth: 'required' });

/** 自分の結果を消す（回答ごと。結果ページも開けなくなる）。 */
export const deleteMyResult = (resultId: string) =>
  v1<void>(`/me/results/${encodeURIComponent(resultId)}`, { method: 'DELETE', auth: 'required' });

/** アカウントを削除すると何が消えるか（確認の窓に出す）。 */
export const fetchDeletionPreview = () =>
  v1<{ results: number; themes: number }>('/me/deletion', { auth: 'required' });

/** アカウントを削除する（GenDan が持っている本人の分）。直近のログインが要る（401 `REAUTH_REQUIRED`）。Firebase のアカウントは画面が消す。 */
export const deleteMyAccount = () => v1<void>('/me', { method: 'DELETE', auth: 'required', body: JSON.stringify({ confirm: true }) });

/** 使い回せる過去の回答（新しい順）。入口ページの「前回の回答を使う」に使う。 */
export const fetchMyAnswers = () => v1<{ sources: readonly ReusableAnswers[] }>('/me/answers', { auth: 'required' });

export const fetchAdminThemes = (query = '') =>
  v1<{ themes: readonly AdminTheme[] }>(`/admin/themes${query ? `?q=${encodeURIComponent(query)}` : ''}`, { auth: 'required' });

export const adminDeleteTheme = (themeId: string, options: { purge?: boolean; note?: string } = {}) =>
  v1<{ themeId: string }>(`/admin/themes/${encodeURIComponent(themeId)}${options.purge ? '?purge=1' : ''}`, {
    method: 'DELETE',
    auth: 'required',
    body: JSON.stringify({ note: options.note ?? '' }),
  });

export const adminRestoreTheme = (themeId: string) =>
  v1<{ themeId: string }>(`/admin/themes/${encodeURIComponent(themeId)}/restore`, { method: 'POST', auth: 'required' });

/**
 * 他の人の診断に絵を描く（ここからだけ）。`force` で、できている絵も捨てて全員を描き直す。
 * `careful` で、描けていない絵だけを版権に気をつけて描く（名前を出さず、オリジナルの見た目で）。
 */
export const adminDrawThemeImages = (themeId: string, force: boolean, careful = false) =>
  v1<ThemeImagesResponse>(`/admin/themes/${encodeURIComponent(themeId)}/images`, { method: 'POST', auth: 'required', body: JSON.stringify({ force, careful }) });

export const fetchAdminAccounts = () => v1<{ accounts: readonly AdminAccount[] }>('/admin/accounts', { auth: 'required' });

export const updateAdminAccount = (uid: string, patch: { role?: 'user' | 'admin'; imageLimitOverride?: number | null }) =>
  v1<{ uid: string }>(`/admin/accounts/${encodeURIComponent(uid)}`, { method: 'PUT', auth: 'required', body: JSON.stringify(patch) });

export const fetchAdminStats = () => v1<AdminStats>('/admin/stats', { auth: 'required' });

export const fetchAdminResults = () => v1<{ results: readonly AdminResult[] }>('/admin/results', { auth: 'required' });

export const adminDeleteResult = (resultId: string) =>
  v1<{ resultId: string }>(`/admin/results/${encodeURIComponent(resultId)}`, { method: 'DELETE', auth: 'required' });

/** サイト全体の生成数の上限と、今日の使用状況（doc/AUTH.md §15）。 */
export const fetchAdminAds = () => v1<AdminAds>('/admin/ads', { auth: 'required' });

export const updateAdminAds = (patch: { adsense?: boolean; affiliate?: boolean; minPlays?: number; items?: readonly Omit<AffiliateItem, 'id'>[] | readonly AffiliateItem[] }) =>
  v1<AdminAds>('/admin/ads', { method: 'PUT', auth: 'required', body: JSON.stringify(patch) });

export const fetchAdminLimits = () => v1<AdminLimits>('/admin/limits', { auth: 'required' });

/** `null` は上限なし。省いた項目はそのまま。 */
export const updateAdminLimits = (patch: {
  themeDailyLimit?: number | null; imageDailyLimit?: number | null;
  anonThemeDailyLimit?: number | null; userThemeDailyLimit?: number | null; userImageDailyLimit?: number | null;
  videoDailyLimit?: number | null; compatDailyLimit?: number | null; userCompatDailyLimit?: number | null;
  compatImageDailyLimit?: number | null; userCompatImageDailyLimit?: number | null;
}) =>
  v1<AdminLimits>('/admin/limits', { method: 'PUT', auth: 'required', body: JSON.stringify(patch) });

export const fetchAdminReports = (all = false) =>
  v1<{ groups: readonly AdminReportGroup[]; minPlays: number }>(`/admin/reports${all ? '?all=1' : ''}`, { auth: 'required' });

export const resolveAdminReports = (themeId: string) =>
  v1<{ themeId: string; resolved: number }>(`/admin/reports/${encodeURIComponent(themeId)}/resolve`, { method: 'POST', auth: 'required' });

export const fetchAdminContacts = () => v1<{ contacts: readonly AdminContact[] }>('/admin/contacts', { auth: 'required' });

export const updateAdminContact = (contactId: string, status: 'open' | 'done') =>
  v1<{ contactId: string; status: 'open' | 'done' }>(`/admin/contacts/${encodeURIComponent(contactId)}`, {
    method: 'PATCH',
    auth: 'required',
    body: JSON.stringify({ status }),
  });

export const fetchAdminLogs = () => v1<{ logs: readonly AdminLog[] }>('/admin/logs', { auth: 'required' });

// ---------------------------------------------------------------- 相性ドラマの動画（管理者だけ）

/** トップページの下に出す動画と SNS のリンク（doc/VIDEO.md §9）。 */
export interface ShowcaseVideo {
  readonly videoId: string;
  readonly url: string;
  readonly title: string;
  readonly characters: readonly { readonly name: string; readonly label: string; readonly emoji: string; readonly portrait: string | null }[];
  readonly themeId: string;
  readonly themeTitle: string;
  readonly aspectRatio: '9:16' | '16:9';
}
export interface ShowcaseLinks { readonly x?: string | null; readonly instagram: string | null; readonly youtube: string | null; readonly tiktok: string | null }

export const fetchShowcase = () => v1<{ videos: readonly ShowcaseVideo[]; links: ShowcaseLinks }>('/showcase');

export const fetchAdminShowcase = () => v1<{ videoIds: readonly string[]; links: ShowcaseLinks; max: number }>('/admin/showcase', { auth: 'required' });

export const saveAdminShowcase = (input: { videoIds: readonly string[]; links: ShowcaseLinks }) =>
  v1<{ videoIds: readonly string[]; links: ShowcaseLinks; max: number }>('/admin/showcase', { method: 'PUT', auth: 'required', body: JSON.stringify(input) });

/** 動画のリクエスト 1 件（本人・管理者の画面）。作りはじめたら `video` に進み具合と、できたら URL が入る。 */
export interface VideoRequestView {
  readonly requestId: string;
  readonly themeId: string;
  /** キャラごとの診断（違う診断のキャラどうしのこともある）。 */
  readonly themeIds?: readonly [string, string];
  /** 2 人が違う診断なら「A × B」。 */
  readonly themeTitle: string;
  readonly characterNames: readonly [string, string];
  readonly prompt: string;
  readonly status: 'pending' | 'accepted' | 'declined' | 'cancelled';
  readonly note: string | null;
  readonly createdAt: string;
  readonly decidedAt: string | null;
  readonly video: { readonly status: string; readonly done: number; readonly total: number; readonly url: string | null } | null;
  readonly videoId?: string | null;
}

/** キャラ 2 人の相性ドラマの動画をリクエストする（要ログイン。運営が選んで作る。doc/VIDEO.md §8）。 */
export const requestVideo = (input: { cast: readonly [CastRef, CastRef]; prompt: string }) =>
  v1<{ request: VideoRequestView }>('/video-requests', { method: 'POST', auth: 'required', appCheck: true, body: JSON.stringify(input) });

export const fetchMyVideoRequests = () => v1<{ requests: readonly VideoRequestView[]; monthlyLimit: number }>('/me/video-requests', { auth: 'required' });

export const cancelVideoRequest = (requestId: string) =>
  v1<void>(`/me/video-requests/${encodeURIComponent(requestId)}`, { method: 'DELETE', auth: 'required' });

export const fetchAdminVideoRequests = () => v1<{ requests: readonly VideoRequestView[] }>('/admin/video-requests', { auth: 'required' });

export const acceptVideoRequest = (requestId: string, input: Record<string, unknown> = {}) =>
  v1<{ requestId: string; videoId: string }>(`/admin/video-requests/${encodeURIComponent(requestId)}/accept`, { method: 'POST', auth: 'required', body: JSON.stringify(input) });

export const declineVideoRequest = (requestId: string, note: string) =>
  v1<{ requestId: string }>(`/admin/video-requests/${encodeURIComponent(requestId)}/decline`, { method: 'POST', auth: 'required', body: JSON.stringify({ note }) });

export const fetchAdminVideos = () => v1<AdminVideos>('/admin/videos', { auth: 'required' });

export const fetchAdminVideo = (videoId: string) => v1<{ video: AdminVideo }>(`/admin/videos/${encodeURIComponent(videoId)}`, { auth: 'required' });

export const createAdminVideo = (input: NewVideo) =>
  v1<{ video: AdminVideo }>('/admin/videos', { method: 'POST', auth: 'required', body: JSON.stringify(input) });

export const retryAdminVideo = (videoId: string, fresh = false) =>
  v1<{ video: AdminVideo }>(`/admin/videos/${encodeURIComponent(videoId)}/retry`, { method: 'POST', auth: 'required', body: JSON.stringify({ fresh }) });

export const regenerateAdminVideoCuts = (videoId: string, cuts: readonly { readonly index: number; readonly count: number }[]) =>
  v1<{ video: AdminVideo }>(`/admin/videos/${encodeURIComponent(videoId)}/regenerate`, { method: 'POST', auth: 'required', body: JSON.stringify({ cuts }) });

export const selectAdminVideoTakes = (videoId: string, selection: readonly number[]) =>
  v1<{ video: AdminVideo }>(`/admin/videos/${encodeURIComponent(videoId)}/selection`, { method: 'PUT', auth: 'required', body: JSON.stringify({ selection }) });

export const cancelAdminVideo = (videoId: string) =>
  v1<{ video: AdminVideo }>(`/admin/videos/${encodeURIComponent(videoId)}/cancel`, { method: 'POST', auth: 'required' });

export const deleteAdminVideo = (videoId: string) =>
  v1<{ videoId: string }>(`/admin/videos/${encodeURIComponent(videoId)}`, { method: 'DELETE', auth: 'required' });

export const fetchVideoChemistry = (a: CastRef, b: CastRef) =>
  v1<VideoChemistry>(
    `/admin/videos/chemistry?themeA=${encodeURIComponent(a.themeId)}&a=${encodeURIComponent(a.characterId)}&themeB=${encodeURIComponent(b.themeId)}&b=${encodeURIComponent(b.characterId)}`,
    { auth: 'required' },
  );
